/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class InAppSlideout
 * @hideconstructor
 * @classdesc API class that is used to manage InAppSlideout
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./InAppSlideoutStateAPI'
], function(APIFactory, InAppSlideoutAPI) {

	function deepClone(obj) {
		return JSON.parse(JSON.stringify(obj));
	}
	class InAppSlideout {
		constructor({ features }) {
			this._features = features;
			this._dashboardApi = features.API;
			this._dashboardState = features.DashboardState;
			this._contentActions = features.ContentActions;
			this._contributions = {
				items: []
			};
			this._stateListeners = [];
			this._previousDashboardState = deepClone(this._dashboardState.getUiState());
			this._dashboardStateIgnoreList = ['dirty'];

			this._api = APIFactory.createAPI(this, [InAppSlideoutAPI]);
			this._dashboardState.onUiStateChange(this._onUiStateChange.bind(this));
			this._updateContributionSelections();
			// @todo: remove this when EPIC CADBLW-6693 is done
			this._dashboardApi.on(('filterDock:expanded'), this.close, this);
		}

		getAPI() {
			return this._api;
		}


		getUiState() {
			return this._dashboardState.getUiState().sidePanel;
		}

		_filterCaredDashboardState(dashboardState) {
			return JSON.stringify(dashboardState, (key, value) => {
				if(this._dashboardStateIgnoreList.includes(key)){
					return undefined;
				}else{
					return value;
				}
			});
		}

		_onUiStateChange(state) {
			const currentCaredDashboardState = this._filterCaredDashboardState(state);
			const previousCaredDashboardState = this._filterCaredDashboardState(this._previousDashboardState);
			if(currentCaredDashboardState !== previousCaredDashboardState){
				this._updateContributionSelections(state.sidePanel);
				this._stateListeners.forEach(fn => fn(state));
			}
			this._previousDashboardState = deepClone(state);
		}

		_updateContributionSelections(state = this.getUiState()) {
			this._contributions.items.forEach(({ contribution, id }) => {
				contribution.selected = state.current === id;
			});
		}

		registerContribution(id, contentContribution) {
			const existing = this.getContribution(id);
			if (existing) {
				throw new Error(`Contribution with id "${id}" is already registered`);
			}
			contentContribution = this._populateContentContribution(id, contentContribution);
			this._registerContentContribution(id, contentContribution);
			this._contributions.items.push({
				id,
				contribution: contentContribution
			});
			this._updateContributionSelections();
		}

		_populateContentContribution(id, contribution) {

			const toggleState = () => {
				const { isOpen, current } = this.getUiState();
				if (isOpen && current === id) {
					this.close();
				} else {
					this.open(id);
				}
			};

			return {
				...contribution,
				checked: this.isOpen(),
				action: toggleState,
				offAction: toggleState,
				selected: false
			};
		}

		_registerContentContribution(id, contentContribution) {
			this._contentActions.registerProvider(id, {
				getContentActionList: (idList = []) => {
					const contributions = [];
					if (contentContribution.isEnabled(idList)) {
						contributions.push(contentContribution);
					} else if (this.getUiState().current === id) {
						this.close();
					}
					return contributions;
				}
			});
		}

		onStateChange(fn) {
			const newLength = this._stateListeners.push(fn);
			const elementIndex = newLength - 1;
			const removeFn = () => {
				this._stateListeners.splice(elementIndex, 1);
			};
			return removeFn;
		}

		removeContribution(id) {
			const index = this._contributions.items.findIndex((item) => item.id === id);
			if (index === -1) {
				throw new Error(`Contribution with id "${id}" does not exist`);
			}
			this._contributions.items.splice(index, 1);
		}

		getContribution(id) {
			return this._contributions.items.find(item => item.id === id);
		}

		getContributions() {
			return this._contributions.items;
		}

		getCurrent() {
			const { current } = this.getUiState();
			return this.getContribution(current);
		}

		isOpen() {
			return this.getUiState().isOpen;
		}

		open(id) {
			if (id) {
				if (this.getContribution(id)) {
					this._dashboardState.setSidePanelCurrentView(id);
				} else {
					throw new Error(`Contribution "${id}" does not exist`);
				}
			}
			if (!this.isOpen()) {
				// @todo: remove the line below when EPIC CADBLW-6693 is done
				this._dashboardApi.triggerDashboardEvent('filterDock:collapse');
				this._dashboardState.setSidePanelOpen(true);
			}
		}

		close() {
			// not need to call setSidePanelCurrentView(null) since it is already implied in setSidePanelOpen(false)
			this._dashboardState.setSidePanelOpen(false);
		}

		destroy() {
			// @todo: remove the line below when EPIC CADBLW-6693 is done
			this._dashboardApi.off('filterDock:expand', this.close, this);
			this._dashboardApi = null;
			this._api = null;
		}
	}

	return InAppSlideout;
});
